/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import debounce from "lodash/debounce";
import { FaPlus, FaFolder, FaFolderOpen, FaFileLines, } from "react-icons/fa6";
import { BsThreeDotsVertical, BsUpload, BsTrash, BsFillFolderFill, BsSortAlphaDown, BsSortAlphaDownAlt } from "react-icons/bs";
import { IoSearch, IoChevronForward, IoClose } from "react-icons/io5";
import { MdDeleteForever } from "react-icons/md";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { LuFolderPlus, LuFolderTree } from "react-icons/lu";
import AddOrgModal from "./AddOrgModal";
import NewFolderModal from "./NewFolderModal";
import MoveUploadModal from "./MoveUploadModal";
import RenameUploadModal from "./RenameUploadModal";
import PreviewUploadModal, { resolveMediaUrl } from "./PreviewUploadModal";
import FolderTreeNode from "./FolderTreeNode";
import TrashBinModal from "./TrashBinModal";
import ManageMembersModal from "./ManageMembersModal";
import { FileIcon, StatusBadge } from "./UploadBadges";
import { useGetAllOrgs, useGetFolderTree, useAddUpdateOrg, useAddUpdateFolder, useDeleteOrg, useGetUploadsByParent, useMoveUpload, useCopyUpload, useUploadFiles, useRenameUpload, useDeleteUpload, useUpdateUploadLock, useUpdateUploadStatus, useUpdateUploadTags, useDeleteForeverOrg, useGetAssignedUsers, useGetUsersList, useMutateAssignedUser, fetchSearchDetails, useGetTrashData, useDeleteForeverUpload } from "./dnsService";
import sweetAlert from "../Common/sweetAlert";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { getMenuPermission } from "../../Redux/SideBarSlice";

/* ─── Main Component ─────────────────────────────────── */
const OrganizationsDNS = ({ isVisible, setSidebarOpen, sidebarOpen, isDark, setIsDark }) => {
  const dispatch = useDispatch();
  const { token, user } = useSelector((state) => state.root.auth);
  const { data } = useSelector((state) => state.root.Sidebar);

  // ── UI state ────────────────────────────────────────────────
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [orgSearch, setOrgSearch] = useState("");
  const [authorFilter, setAuthorFilter] = useState(""); // filter orgs by createdUserName
  const [fileSearch, setFileSearch] = useState("");
  const [showAddOrg, setShowAddOrg] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [fileActionId, setFileActionId] = useState(null);
  const [fileActionPos, setFileActionPos] = useState({ top: 0, right: 0 });
  const [orgContextMenuId, setOrgContextMenuId] = useState(null);
  const [orgContextMenuPos, setOrgContextMenuPos] = useState({ top: 0, left: 0 });
  const [editingOrg, setEditingOrg] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [, setShowMoveModal] = useState(false);
  const [isCopy, setIsCopy] = useState(false);
  const [movingItem, setMovingItem] = useState(null); // { id, name, type: 'Folder' | 'File' }
  const [targetParentId, setTargetParentId] = useState("");
  // Folder tree expansion/options states
  const [showFolderOptions, setShowFolderOptions] = useState(false);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [trashActiveTab, setTrashActiveTab] = useState("orgs"); // 'orgs' | 'uploads'
  const [isSorted, setIsSorted] = useState(false);
  const [orgSortOrder, setOrgSortOrder] = useState("default"); // 'default' | 'asc' | 'desc'
  const [pendingNavigation, setPendingNavigation] = useState(null); // { folderId, orgId }
  const [expandedFolderIds, setExpandedFolderIds] = useState(new Set());
  // Rename modal state
  const [renameModal, setRenameModal] = useState(null); // { id, name, type: 'Folder' | 'File' }
  const [renameValue, setRenameValue] = useState("");
  // Preview modal state
  const [previewFile, setPreviewFile] = useState(null);
  // Tags modal state
  const [tagsModal, setTagsModal] = useState(null); // { id, name, tags }
  const [tagsValue, setTagsValue] = useState("");
  const [permissions, setPermissions] = useState({
    isView: false,
    isSave: false,
    isDelete: false,
  });
  // Manage members modal state
  const [showManageMembers, setShowManageMembers] = useState(false);
  const [memberOrg, setMemberOrg] = useState(null);
  // When set, "Manage Members" is opened for a specific folder inside memberOrg
  const [memberFolder, setMemberFolder] = useState(null);
  const [selectedUserToAssign, setSelectedUserToAssign] = useState("");
  // Single permission state: checked = selected, allowed = role permits (from GetAllLevelData)
  const [memberPermissions, setMemberPermissions] = useState({
    isView: { checked: true, allowed: false },
    isEdit: { checked: false, allowed: false },
    isDelete: { checked: false, allowed: false },
  });
  const [rolePermLoading, setRolePermLoading] = useState(false);
  const [selectedUploadIds, setSelectedUploadIds] = useState([]);

  useEffect(() => {
    setSelectedUploadIds([]);
  }, [selectedFolder, page, fileSearch]);

  // ── Global Search state ─────────────────────────────────────
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchRef = useRef(null);
  const searchAbortRef = useRef(null);

  const fileActionRef = useRef(null);
  const orgContextMenuRef = useRef(null);
  const uploadInputRef = useRef(null);

  // ── Module permissions (isView / isSave / isDelete) ──────────
  useEffect(() => {
    const loadPermissions = async () => {
      if (!user?.userRoleID) return;

      let sidebarData = data?.bottummenu ?? []
      const moduleItem = sidebarData?.find(item => item?.pageName === "Organizations");

      const permissionRes = await dispatch(getMenuPermission({ UserRoleID: user.userRoleID, ModuleID: moduleItem?.moduleID ?? 0 }));
      const accessList = permissionRes?.payload?.data;
      if (Array.isArray(accessList) && accessList.length > 0) {
        setPermissions({
          isView: !!accessList[0].isView,
          isSave: !!accessList[0].isSave,
          isDelete: !!accessList[0].isDelete,
        });
      }
    };

    loadPermissions();
  }, [dispatch, user?.userRoleID, data]);

  // ── React Query ──────────────────────────────────────────────
  const { data: orgs = [], isLoading: orgsLoading } = useGetAllOrgs(token);


  const activeOrgs = useMemo(() => {
    if (!Array.isArray(orgs)) return [];
    const nonDeleted = orgs.filter((o) => !o.isDeleted);
    // if (user?.parentID === 0) return nonDeleted; // admin sees all
    return nonDeleted;
    // return nonDeleted.filter((o) => o.userID === user?.userID);
  }, [orgs, user]);

  // Soft-deleted orgs shown inside the Trash Bin modal
  const deletedOrgs = useMemo(
    () => (Array.isArray(orgs) ? orgs.filter((o) => o.isDeleted) : []),
    [orgs]
  );
  const { data: treeResponse = { model: [], organizations: [] }, isLoading: treeLoading } = useGetFolderTree(
    token,
    selectedOrg?.organizationsDNSId
  );
  // Never show a tree when there is no active (non-deleted) selection
  const folderTree = selectedOrg ? (treeResponse?.model ?? []) : [];
  const orgStorageDetails = selectedOrg ? (treeResponse?.organizations?.[0] ?? null) : null;

  // 1st priority: selected org assigned perms | 2nd: role GetAllLevelData perms
  const effectivePermissions = useMemo(() => {
    const assigned = orgStorageDetails?.assigned;
    if (assigned && typeof assigned === "object") {
      return {
        isView: !!assigned.isView,
        isEdit: !!assigned.isEdit,
        isDelete: !!assigned.isDelete,
        isSave: !!assigned.isEdit,
      };
    }
    return {
      isView: !!permissions.isView,
      isEdit: !!permissions.isSave,
      isDelete: !!permissions.isDelete,
      isSave: !!permissions.isSave,
    };
  }, [orgStorageDetails?.assigned, permissions]);

  // ── Find the root-level folder ancestor for a given folder ──
  const findRootFolderForNode = (folderId, tree) => {
    for (const rootNode of tree) {
      const rootId = rootNode.folderDNSID ?? 0;
      if (rootId === folderId) return rootNode;
      const findInChildren = (node, targetId) => {
        if (!node.children) return false;
        for (const child of node.children) {
          const cId = child.folderDNSID ?? child.folderID;
          if (cId === targetId) return true;
          if (findInChildren(child, targetId)) return true;
        }
        return false;
      };
      if (findInChildren(rootNode, folderId)) return rootNode;
    }
    return null;
  };

  const folderPermissions = useMemo(() => {
    const folderId = selectedFolder?.folderDNSID ?? 0;
    if (!folderId || !folderTree.length) return effectivePermissions;

    if (user?.parentID === 0) return effectivePermissions;

    const rootFolder = findRootFolderForNode(folderId, folderTree);
    if (!rootFolder || !rootFolder.assignedUsers?.length) return effectivePermissions;

    const userAssignment = rootFolder.assignedUsers[0]
    if (!userAssignment) {
      return { isView: false, isEdit: false, isDelete: false, isSave: false };
    }

    return {
      isView: !!userAssignment?.isView,
      isEdit: !!userAssignment?.isEdit,
      isDelete: !!userAssignment?.isDelete,
      isSave: !!userAssignment?.isEdit,
    };
  }, [selectedFolder, folderTree, effectivePermissions, user]);

  const addUpdateOrgMutation = useAddUpdateOrg(token);
  const addUpdateFolderMutation = useAddUpdateFolder(token, selectedOrg?.organizationsDNSId);
  const deleteOrgMutation = useDeleteOrg(token);

  const isFolderSelected = !!(selectedFolder?.folderDNSID || selectedFolder?.folderID);

  // Fetch uploads dynamically for selected folder or parent
  const parentIdForUploads = isFolderSelected ? (selectedFolder?.folderDNSID ?? selectedFolder?.folderID) : undefined;
  // const parentIdForUploads = selectedFolder?.folderDNSID ?? 0;
  const { data: dynamicUploads = [] } = useGetUploadsByParent(
    token,
    selectedOrg?.organizationsDNSId,
    parentIdForUploads
  );

  const moveUploadMutation = useMoveUpload(token, selectedOrg?.organizationsDNSId);
  const copyUploadMutation = useCopyUpload(token, selectedOrg?.organizationsDNSId);
  const uploadFilesMutation = useUploadFiles(token, selectedOrg?.organizationsDNSId);
  const renameUploadMutation = useRenameUpload(token, selectedOrg?.organizationsDNSId);
  const deleteUploadMutation = useDeleteUpload(token, selectedOrg?.organizationsDNSId);
  const updateUploadLockMutation = useUpdateUploadLock(token, selectedOrg?.organizationsDNSId);
  const updateUploadStatusMutation = useUpdateUploadStatus(token, selectedOrg?.organizationsDNSId);
  const updateUploadTagsMutation = useUpdateUploadTags(token, selectedOrg?.organizationsDNSId);
  const deleteForeverOrgMutation = useDeleteForeverOrg(token);
  const { data: trashData = [], isLoading: trashLoading } = useGetTrashData(token, selectedOrg?.organizationsDNSId);
  const deleteForeverUploadMutation = useDeleteForeverUpload(token, selectedOrg?.organizationsDNSId);
  const { data: assignedUsers = [] } = useGetAssignedUsers(
    token,
    memberOrg?.organizationsDNSId,
    memberFolder?.folderDNSID ?? memberFolder?.folderID ?? null
  );
  const { data: allUsers = [] } = useGetUsersList(token, user?.userID);
  const mutateAssignedUserMutation = useMutateAssignedUser(
    token,
    memberOrg?.organizationsDNSId,
    memberFolder?.folderDNSID ?? memberFolder?.folderID ?? null
  );

  // Show only members bound to the current scope:
  //  - Folder-level modal → records whose folderDNSID matches the opened folder
  //  - Org-level modal    → records without a folderDNSID (org-scope only)
  const activeFolderIdForMembers =
    memberFolder?.folderDNSID ?? memberFolder?.folderID ?? null;
  const visibleAssignedUsers = useMemo(() => {
    if (!Array.isArray(assignedUsers)) return [];
    if (activeFolderIdForMembers) {
      return assignedUsers.filter(
        (u) => Number(u?.folderDNSID) === Number(activeFolderIdForMembers)
      );
    }
    return assignedUsers.filter(
      (u) => !u?.folderDNSID || Number(u.folderDNSID) === 0
    );
  }, [assignedUsers, activeFolderIdForMembers]);

  // When assign-user is selected → call GetAllLevelData by that user's userRoleID
  useEffect(() => {
    const emptyPerms = {
      isView: { checked: false, allowed: false },
      isEdit: { checked: false, allowed: false },
      isDelete: { checked: false, allowed: false },
    };

    const loadSelectedUserRolePermissions = async () => {
      if (!selectedUserToAssign) {
        setMemberPermissions(emptyPerms);
        setRolePermLoading(false);
        return;
      }

      const selectedUser = allUsers.find(
        (u) => String(u.userID) === String(selectedUserToAssign)
      );
      const roleId = selectedUser?.userRoleID ?? 0;
      if (!roleId) {
        toast.error("Selected user has no role.");
        setMemberPermissions(emptyPerms);
        return;
      }

      const sidebarData = data?.bottummenu ?? [];
      const moduleItem = sidebarData.find((item) => item?.pageName === "Organizations");
      const moduleID = moduleItem?.moduleID ?? 0;

      try {
        setRolePermLoading(true);
        const permissionRes = await dispatch(
          getMenuPermission({ UserRoleID: roleId, ModuleID: moduleID })
        );
        const accessList = permissionRes?.payload?.data;
        const access = Array.isArray(accessList) && accessList.length > 0 ? accessList[0] : null;

        const canView = !!access?.isView;
        const canEdit = !!(access?.isEdit ?? access?.isSave);
        const canDelete = !!access?.isDelete;

        // View locked checked when allowed; Edit/Delete start unchecked (user can select)
        setMemberPermissions({
          isView: { checked: canView, allowed: canView },
          isEdit: { checked: canEdit, allowed: canEdit },
          isDelete: { checked: canDelete, allowed: canDelete },
        });
      } catch {
        toast.error("Failed to load role permissions.");
        setMemberPermissions(emptyPerms);
      } finally {
        setRolePermLoading(false);
      }
    };

    loadSelectedUserRolePermissions();
  }, [selectedUserToAssign, allUsers, data, dispatch]);


  // Sync selection to active (non-deleted) orgs only — soft-deleted stay in `orgs` for Trash
  useEffect(() => {
    if (activeOrgs.length === 0) {
      if (selectedOrg) {
        setSelectedOrg(null);
        setSelectedFolder(null);
        setBreadcrumb([]);
      }
      return;
    }
    if (!selectedOrg) {
      setSelectedOrg(activeOrgs[0]);
      setBreadcrumb([{ name: activeOrgs[0].organizationsName, node: null }]);
      return;
    }
    const stillActive = activeOrgs.some(
      (o) => o.organizationsDNSId === selectedOrg.organizationsDNSId
    );
    if (!stillActive) {
      setSelectedOrg(activeOrgs[0]);
      setBreadcrumb([{ name: activeOrgs[0].organizationsName, node: null }]);
    }
  }, [activeOrgs]);

  // Reset selected folder when org changes
  useEffect(() => {
    setSelectedFolder(null);
    setExpandedFolderIds(new Set());
    if (selectedOrg) setBreadcrumb([{ name: selectedOrg.organizationsName, node: null }]);
  }, [selectedOrg?.organizationsDNSId]);

  // Auto-expand first level nodes when tree loads
  // useEffect(() => {
  //   if (folderTree.length > 0 && expandedFolderIds.size === 0) {
  //     const initial = new Set();
  //     folderTree.forEach((node) => {
  //       const id = node.folderDNSID ?? node.folderID ?? node.id;
  //       initial.add(id);
  //     });
  //     setExpandedFolderIds(initial);
  //   }
  // }, [folderTree]);

  // Close all menus on outside click
  useEffect(() => {
    const close = (e) => {
      // If clicking a three dots trigger button, let its own click handler handle it
      if (e.target.closest("button")) {
        return;
      }
      if (fileActionRef.current && !fileActionRef.current.contains(e.target)) {
        setFileActionId(null);
      }
      if (orgContextMenuRef.current && !orgContextMenuRef.current.contains(e.target)) {
        setOrgContextMenuId(null);
      }
      setShowFolderOptions(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  // Listen for openMoveModal events from TreeNode components
  useEffect(() => {
    const handleOpen = (e) => {
      setMovingItem(e.detail);
      setIsCopy(!!e.detail.isCopy);
      setTargetParentId("");
      setShowMoveModal(true);
    };
    window.addEventListener("openMoveModal", handleOpen);
    return () => window.removeEventListener("openMoveModal", handleOpen);
  }, []);

  // Close search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close file context menu on scroll to prevent floating disconnects
  useEffect(() => {
    const handleScroll = () => {
      if (fileActionId) setFileActionId(null);
    };
    // Use capture phase to catch scrolls on any internal container
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [fileActionId]);

  // Debounced search API call using lodash debounce
  const debouncedSearchApi = useMemo(
    () =>
      debounce(async (query, orgId) => {
        // Abort any in-flight request
        if (searchAbortRef.current) searchAbortRef.current.abort();
        const controller = new AbortController();
        searchAbortRef.current = controller;
        try {
          const result = await fetchSearchDetails(token, orgId, query.trim());
          if (!controller.signal.aborted) {
            const items = result?.model ?? result?.data ?? result ?? [];
            setSearchSuggestions(Array.isArray(items) ? items : []);
            setSearchLoading(false);
          }
        } catch (err) {
          if (!controller.signal.aborted) {
            setSearchSuggestions([]);
            setSearchLoading(false);
          }
        }
      }, 500),
    [token]
  );

  // Input change handler — updates UI instantly, debounces API call
  const handleGlobalSearch = (value) => {
    setGlobalSearchQuery(value);
    if (!value.trim() || !selectedOrg) {
      debouncedSearchApi.cancel();
      setSearchSuggestions([]);
      setShowSearchDropdown(false);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    setShowSearchDropdown(true);
    debouncedSearchApi(value, selectedOrg.organizationsDNSId);
  };

  // Cleanup debounce and abort on unmount
  useEffect(() => {
    return () => {
      debouncedSearchApi.cancel();
      if (searchAbortRef.current) searchAbortRef.current.abort();
    };
  }, [debouncedSearchApi]);

  // Effect to handle cross-organization search redirection once the folder tree loads
  useEffect(() => {
    if (!pendingNavigation || treeLoading || !folderTree.length) return;

    if (String(selectedOrg?.organizationsDNSId) === String(pendingNavigation.orgId)) {
      const targetId = pendingNavigation.folderId;

      const findFolder = (nodes, targetId) => {
        for (const n of nodes) {
          const nId = n.folderDNSID ?? n.folderID ?? n.id;
          if (String(nId) === String(targetId)) return n;
          if (n.children?.length) {
            const found = findFolder(n.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      const folder = findFolder(folderTree, targetId);
      if (folder) {
        const expandAncestors = (nodes, targetId, path = []) => {
          for (const n of nodes) {
            const nId = n.folderDNSID ?? n.folderID ?? n.id;
            if (String(nId) === String(targetId)) return [...path, nId];
            if (n.children?.length) {
              const result = expandAncestors(n.children, targetId, [...path, nId]);
              if (result) return result;
            }
          }
          return null;
        };

        const ancestorPath = expandAncestors(folderTree, targetId) || [];
        setExpandedFolderIds((prev) => {
          const next = new Set(prev);
          ancestorPath.forEach((id) => next.add(id));
          return next;
        });
        setSelectedFolder(folder);
        const fId = folder.folderDNSID ?? folder.folderID ?? folder.id;
        setBreadcrumb(buildBreadcrumbPath(fId));
      } else {
        handleSelectOrg(selectedOrg);
      }
      setPendingNavigation(null); // Clear pending navigation
    }
  }, [folderTree, pendingNavigation, selectedOrg, treeLoading]);

  // Handle suggestion click — navigate to parent folder or folder itself
  const handleSearchSuggestionClick = (item) => {
    setGlobalSearchQuery("");
    setShowSearchDropdown(false);
    setSearchSuggestions([]);

    const targetOrgId = item.organizationsDNSId ?? item.organizationsDnsId;
    const isFolder = (item.fileType || item.type || "").toLowerCase() === "folder" || (!item.parentID && !item.parentId);

    // If it's a folder, navigate to the folder itself; if it's a file, navigate to its parent folder.
    const targetFolderId = isFolder
      ? (item.uploadID ?? item.folderDNSID ?? item.id)
      : (item.parentID ?? item.parentId ?? 0);

    // If suggestion belongs to a different organization, switch org first and schedule navigation
    if (targetOrgId && String(targetOrgId) !== String(selectedOrg?.organizationsDNSId)) {
      const targetOrg = orgs.find(o => String(o.organizationsDNSId) === String(targetOrgId));
      if (targetOrg) {
        setPendingNavigation({
          folderId: targetFolderId,
          orgId: targetOrgId
        });
        handleSelectOrg(targetOrg);
        setPage(1);
        return;
      }
    }

    // Otherwise, navigate folder tree of current organization directly
    const findFolder = (nodes, targetId) => {
      for (const n of nodes) {
        const nId = n.folderDNSID ?? n.folderID ?? n.id;
        if (nId === targetId) return n;
        if (n.children?.length) {
          const found = findFolder(n.children, targetId);
          if (found) return found;
        }
      }
      return null;
    };
    const folder = findFolder(folderTree, targetFolderId);

    console.log("🚀 ~ handleSearchSuggestionClick ~ folder:", folder)
    if (folder) {
      // Expand all ancestors up to this folder
      const expandAncestors = (nodes, targetId, path = []) => {
        for (const n of nodes) {
          const nId = n.folderDNSID ?? n.folderID ?? n.id;
          if (String(nId) === String(targetId)) return [...path, nId];
          if (n.children?.length) {
            const result = expandAncestors(n.children, targetId, [...path, nId]);
            if (result) return result;
          }
        }
        return null;
      };
      const ancestorPath = expandAncestors(folderTree, targetFolderId) || [];
      setExpandedFolderIds((prev) => {
        const next = new Set(prev);
        ancestorPath.forEach((id) => next.add(id));
        return next;
      });
      setSelectedFolder(folder);
      const fId = folder.folderDNSID ?? folder.folderID ?? folder.id;
      setBreadcrumb(buildBreadcrumbPath(fId));
    } else {
      // If parent is 0 or root, go to org root
      handleSelectOrg(selectedOrg);
    }
    setPage(1);
  };

  // Folder Action Helpers
  const toggleFolderExpanded = (folderId) => {
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleExpandAll = () => {
    const allIds = new Set();
    const traverse = (nodes) => {
      nodes.forEach((n) => {
        const id = n.folderDNSID ?? n.folderID ?? n.id;
        allIds.add(id);
        if (n.children?.length > 0) {
          traverse(n.children);
        }
      });
    };
    traverse(folderTree);
    setExpandedFolderIds(allIds);
  };

  const handleCollapseAll = () => {
    setExpandedFolderIds(new Set());
  };

  const sortTreeNodes = (nodes) => {
    if (!nodes) return [];
    return [...nodes]
      .sort((a, b) => {
        const nameA = (a.folderName ?? a.name ?? "").toLowerCase();
        const nameB = (b.folderName ?? b.name ?? "").toLowerCase();
        return nameA.localeCompare(nameB);
      })
      .map((n) => ({
        ...n,
        children: n.children ? sortTreeNodes(n.children) : [],
      }));
  };

  const renderedTree = isSorted ? sortTreeNodes(folderTree) : folderTree;



  // Unique author names for filter dropdown
  const authorOptions = useMemo(() => {
    const names = new Set();
    activeOrgs.forEach((o) => {
      if (o.createdUserName) names.add(o.createdUserName);
    });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [activeOrgs]);

  // Org list filtered by search + author + sorted by orgSortOrder
  const filteredOrgs = useMemo(() => {
    const filtered = activeOrgs.filter((o) => {
      const matchesSearch = (o.organizationsName || "").toLowerCase().includes(orgSearch.toLowerCase());
      const matchesAuthor = !authorFilter || o.createdUserName === authorFilter;
      return matchesSearch && matchesAuthor;
    });

    if (orgSortOrder === "asc") {
      return [...filtered].sort((a, b) =>
        (a.organizationsName || "").localeCompare(b.organizationsName || "")
      );
    } else if (orgSortOrder === "desc") {
      return [...filtered].sort((a, b) =>
        (b.organizationsName || "").localeCompare(a.organizationsName || "")
      );
    }
    return filtered;
  }, [activeOrgs, orgSearch, authorFilter, orgSortOrder]);

  // const filteredOrgs = activeOrgs.filter((o) => {
  //   const matchesSearch = (o.organizationsName || "").toLowerCase().includes(orgSearch.toLowerCase());
  //   const matchesAuthor = !authorFilter || o.createdUserName === authorFilter;
  //   return matchesSearch;
  // });


  // Files come from dynamic uploads API call first, falling back to node.files
  const rawFiles = dynamicUploads.length > 0 ? dynamicUploads : (selectedFolder?.files ?? []);
  const filteredFiles = rawFiles.filter((f) =>
    (f.fileName || "").toLowerCase().includes(fileSearch.toLowerCase())
  );

  // ── Color palette for org avatars ────────────────────────────
  const ORG_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#3b82f6", "#ef4444", "#a855f7", "#f97316", "#06b6d4"];
  const getOrgColor = (index) => ORG_COLORS[index % ORG_COLORS.length];
  const getOrgInitials = (name = "") => name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);

  // ── Handlers ─────────────────────────────────────────────────
  const handleAddOrg = async (form) => {
    const payload = {
      organizationsDNSId: editingOrg ? editingOrg.organizationsDNSId : 0,
      organizationsName: form.name,
      organizationsCode: form.code,
      description: form.desc,
      storageLimit: parseInt(form.storage) || 50,
      isActive: form.status,
    };
    try {
      await addUpdateOrgMutation.mutateAsync(payload);
      setShowAddOrg(false);
      setEditingOrg(null);
      toast.success(editingOrg ? "OrganizationsDNS Updated Successfully!" : "OrganizationsDNS Saved Successfully!");
    } catch {
      toast.error(editingOrg ? "Failed to update organization." : "Failed to add organization.");
    }
  };

  const handleDeleteOrg = async (org) => {
    const id = org.organizationsDNSId;
    const orgName = org.organizationsName || "Organization";
    sweetAlert.confirm(
      "Delete Organization?",
      `This moves ${orgName} and all its folders/files to Trash. You can restore it within the retention window.`,
      "delete"
    ).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteOrgMutation.mutateAsync({ id, isDeleted: true });
          toast.success(`"${orgName}" moved to Trash successfully!`);
          if (selectedOrg?.organizationsDNSId === id) {
            setSelectedOrg(null);
          }
        } catch {
          toast.error("Failed to delete organization.");
        }
      }
    });
  };

  const handleRestoreOrg = async (id, orgName) => {
    try {
      await deleteOrgMutation.mutateAsync({ id, isDeleted: false });
      toast.success(`"${orgName}" restored successfully!`);
    } catch {
      toast.error("Failed to restore organization.");
    }
  };

  const handleDeleteOrgForever = async (id, orgName) => {
    sweetAlert.confirm(
      "Delete Forever?",
      `Are you sure you want to permanently delete "${orgName}"? This action cannot be undone.`,
      "delete"
    ).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteForeverOrgMutation.mutateAsync(id);
          toast.success(`"${orgName}" permanently deleted.`);
        } catch {
          toast.error("Failed to permanently delete organization.");
        }
      }
    });
  };

  const handleRestoreUpload = async (id, name, type) => {
    try {
      const payload = [{ Type: type, UploadIDs: String(id), isDeleted: false }];
      await deleteUploadMutation.mutateAsync({ payload });
      toast.success(`"${name}" restored successfully!`);
    } catch {
      toast.error("Failed to restore item.");
    }
  };

  const handleDeleteUploadForever = async (id, name, type) => {
    sweetAlert.confirm(
      "Delete Forever?",
      `Are you sure you want to permanently delete "${name}"? This action cannot be undone.`,
      "delete"
    ).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const payload = [{ type: type, uploadIDs: String(id), isDeleted: true }];
          await deleteForeverUploadMutation.mutateAsync(payload);
          toast.success(`"${name}" permanently deleted.`);
        } catch {
          toast.error("Failed to permanently delete item.");
        }
      }
    });
  };


  const handleAddMember = async () => {
    if (!selectedUserToAssign) {
      toast.error("Please select a user to assign.");
      return;
    }
    if (!memberPermissions.isView.checked && !memberPermissions.isEdit.checked && !memberPermissions.isDelete.checked) {
      toast.error("Please select at least one permission.");
      return;
    }
    const folderIdForPayload =
      memberFolder?.folderDNSID ?? memberFolder?.folderID ?? null;
    const payload = {
      organizationsDNSId: memberOrg.organizationsDNSId,
      ...(folderIdForPayload ? { folderDNSID: folderIdForPayload } : {}),
      assignedUserId: parseInt(selectedUserToAssign),
      operation: "Insert",
      isView: !!memberPermissions.isView.checked,
      isEdit: !!memberPermissions.isEdit.checked,
      isDelete: !!memberPermissions.isDelete.checked,
    };
    try {
      await mutateAssignedUserMutation.mutateAsync(payload);
      toast.success("User assigned successfully!");
      setSelectedUserToAssign("");
      setMemberPermissions({
        isView: { checked: false, allowed: false },
        isEdit: { checked: false, allowed: false },
        isDelete: { checked: false, allowed: false },
      });
    } catch {
      toast.error("Failed to assign user.");
    }
  };

  const toggleMemberPermission = (key) => {
    // View is always locked; API-false (allowed=false) cannot be toggled
    if (key === "isView") return;
    setMemberPermissions((prev) => {
      if (!prev[key]?.allowed) return prev;
      return {
        ...prev,
        [key]: { ...prev[key], checked: !prev[key].checked },
      };
    });
  };

  const resetMemberModal = () => {
    setShowManageMembers(false);
    setMemberOrg(null);
    setMemberFolder(null);
    setSelectedUserToAssign("");
    setMemberPermissions({
      isView: { checked: false, allowed: false },
      isEdit: { checked: false, allowed: false },
      isDelete: { checked: false, allowed: false },
    });
    setRolePermLoading(false);
  };

  // Open Manage Members modal for a specific folder inside the currently selected org
  const openFolderManageMembers = (folderNode) => {
    if (!selectedOrg || !folderNode) return;
    setMemberOrg(selectedOrg);
    setMemberFolder(folderNode);
    setShowManageMembers(true);
  };

  const handleRemoveMember = async (assignedId, userId) => {
    const payload = {
      organizationsDNSAssignedId: assignedId,
      assignedUserId: userId,
      operation: "Delete",
    };
    try {
      await mutateAssignedUserMutation.mutateAsync(payload);
      toast.success("User removed successfully!");
    } catch {
      toast.error("Failed to remove user.");
    }
  };

  const handleMoveConfirm = async () => {
    if (!movingItem || !targetParentId) return;
    try {
      if (isCopy) {
        await copyUploadMutation.mutateAsync({
          type: movingItem.type,
          uploadId: movingItem.id,
          newParentId: parseInt(targetParentId),
        });
        toast.success(`${movingItem.type} Copied Successfully!`);
      } else {
        await moveUploadMutation.mutateAsync({
          type: movingItem.type,
          uploadId: movingItem.id,
          newParentId: parseInt(targetParentId),
        });
        toast.success(`${movingItem.type} Moved Successfully!`);
      }
      setShowMoveModal(false);
      setMovingItem(null);
    } catch {
      toast.error(`Failed to ${isCopy ? "copy" : "move"} ${movingItem.type.toLowerCase()}.`);
    }
  };

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    if (!selectedOrg) {
      toast.error("Please select an organization first.");
      return;
    }
    const parentId = selectedFolder?.folderDNSID ?? 0;
    const toastId = toast.loading(`Uploading ${files.length} file${files.length > 1 ? "s" : ""}...`, { duration: Infinity });
    try {
      await uploadFilesMutation.mutateAsync({ parentId, files });
      toast.success(`${files.length} file${files.length > 1 ? "s" : ""} uploaded successfully!`, { id: toastId });
      toast.remove()
    } catch {
      toast.error("Upload failed. Please try again.", { id: toastId });
    } finally {
      // Reset the input so the same file can be re-uploaded if needed
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    }
  };

  // ── Rename handler ────────────────────────────────────────────
  const openRenameModal = (id, name, type) => {
    setRenameModal({ id, name, type });
    setRenameValue(name);
  };

  const handleRenameConfirm = async () => {
    if (!renameModal || !renameValue.trim()) return;
    try {
      await renameUploadMutation.mutateAsync({
        type: renameModal.type,
        uploadId: renameModal.id,
        fileName: renameValue.trim(),
      });
      toast.success(`Renamed to "${renameValue.trim()}" successfully!`);
      setRenameModal(null);
    } catch {
      toast.error("Rename failed. Please try again.");
    }
  };

  // ── Delete upload (folder / file / media) handler ─────────────
  const handleDeleteUpload = (id = null, name = "", type = "Multiple") => {
    const isBulk = id === null || typeof id === "object";
    const actualId = isBulk ? null : id;
    const idsToDelete = isBulk ? selectedUploadIds : [actualId];

    if (idsToDelete.length === 0) return;

    const title = isBulk ? "Delete Selected" : `Delete ${type}`;
    const text = isBulk
      ? `Are you sure you want to delete ${idsToDelete.length} item(s)? This cannot be undone.`
      : `Are you sure you want to delete "${name}"? This cannot be undone.`;

    sweetAlert.confirm(title, text, "delete").then(async (result) => {
      if (result.isConfirmed) {
        try {
          const payload = [];
          if (isBulk) {
            const folders = [];
            const files = [];
            idsToDelete.forEach(uid => {
              const fileObj = paginatedFiles.find(f => f.uploadID === uid);
              if (fileObj) {
                if (fileObj.fileType === "Folder") folders.push(uid);
                else files.push(uid);
              }
            });
            if (folders.length > 0) payload.push({ Type: "Folder", UploadIDs: folders.join(','), isDeleted: true });
            if (files.length > 0) payload.push({ Type: "File", UploadIDs: files.join(','), isDeleted: true });
          } else {
            payload.push({ Type: type, UploadIDs: String(id), isDeleted: true });
          }

          await deleteUploadMutation.mutateAsync({ payload });
          toast.success(isBulk ? `${idsToDelete.length} item(s) deleted successfully!` : `"${name}" deleted successfully!`);
          if (isBulk) setSelectedUploadIds([]);
        } catch {
          toast.error(isBulk ? `Failed to delete selected items.` : `Failed to delete ${type.toLowerCase()}.`);
        }
      }
    });
  };

  const handleToggleUploadLock = async (uploadId, currentLocked) => {
    const isLocked = !currentLocked;
    try {
      await updateUploadLockMutation.mutateAsync({ uploadId, isLocked });
      toast.success(isLocked ? "Locked (Checked-out) successfully!" : "Unlocked successfully!");
    } catch {
      toast.error(isLocked ? "Failed to lock." : "Failed to unlock.");
    }
  };

  const handleToggleUploadStatus = async (uploadId, currentStatus) => {
    const status = currentStatus === "Published" ? "Draft" : "Published";
    try {
      await updateUploadStatusMutation.mutateAsync({ uploadId, status });
      toast.success(`Status updated to ${status} successfully!`);
    } catch {
      toast.error("Failed to update status.");
    }
  };

  const handleUpdateTags = async () => {
    if (!tagsModal) return;
    try {
      await updateUploadTagsMutation.mutateAsync({
        uploadId: tagsModal.id,
        tags: tagsValue.trim(),
      });
      toast.success("Tags updated successfully!");
      setTagsModal(null);
    } catch {
      toast.error("Failed to update tags.");
    }
  };

  const handleFileOrFolderClick = (file) => {
    if (file.fileType === "Folder") {
      const folderNode = {
        folderDNSID: file.uploadID,
        folderName: file.fileName,
        parentId: file.parentId,
      };
      handleSelectFolder(folderNode);
    } else {
      setPreviewFile(file);
    }
  };

  const handleAddFolder = async (form) => {
    if (!selectedOrg) return;
    console.log("🚀 ~ handleAddFolder ~ selectedOrg:", selectedOrg)
    console.log("🚀 ~ handleAddFolder ~ selectedFolder:", selectedFolder)
    const payload = {
      folderDNSID: 0,
      folderName: form.name,
      fileSize: "0B",
      parentId: selectedFolder?.folderDNSID ?? 0,
      userID: user?.userID ?? 0,
      organizationsDNSId: selectedOrg.organizationsDNSId,
      operation: "Insert",
      folderIconId: form.icon + 1,
      description: form.desc,
    };
    try {
      await addUpdateFolderMutation.mutateAsync(payload);
      toast.success("Folder created!");
    } catch {
      toast.error("Failed to create folder.");
    }
  };

  const handleSelectOrg = (org) => {
    console.log("🚀 ~ handleSelectOrg ~ org:", org)
    setSelectedOrg(org);
    setBreadcrumb([{ name: org?.organizationsName, node: null }]);
    setSelectedFolder(org);
    setPage(1);
  };

  // Build full breadcrumb path from root to target folder
  const buildBreadcrumbPath = (targetFolderId) => {
    const orgCrumb = { name: selectedOrg?.organizationsName || "Organization", node: null };
    if (!targetFolderId) return [orgCrumb];

    const findPath = (nodes, targetId, path = []) => {
      for (const n of nodes) {
        const nId = n.folderDNSID ?? n.folderID ?? n.id;
        const current = { name: n.folderName ?? n.name, node: n };
        if (nId === targetId) return [...path, current];
        if (n.children?.length) {
          const result = findPath(n.children, targetId, [...path, current]);
          if (result) return result;
        }
      }
      return null;
    };

    const folderPath = findPath(folderTree, targetFolderId) || [];
    return [orgCrumb, ...folderPath];
  };

  const handleSelectFolder = (node) => {
    setSelectedFolder(node);
    const folderId = node.folderDNSID ?? node.folderID ?? node.id;
    setBreadcrumb(buildBreadcrumbPath(folderId));
    setPage(1);
  };

  const sortedFiles = isSorted
    ? [...filteredFiles].sort((a, b) => {
      const nameA = (a.fileName || "").toLowerCase();
      const nameB = (b.fileName || "").toLowerCase();
      return nameA.localeCompare(nameB);
    })
    : filteredFiles;

  const totalItems = sortedFiles.length;
  const paginatedFiles = sortedFiles.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(totalItems / perPage);

  return (
    <>
      <div className="flex flex-1">
        <div className="page-wrapper relative ml-auto w-[calc(100%-326px)] px-4 pt-[54px] duration-300">
          {/* Page Header */}
          <div className="crm-page-header">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="crm-page-title">Organizations</h1>
                  <HiOutlineInformationCircle size={16} className="text-slate-400 mt-0.5" />
                </div>
                <p className="text-xs text-slate-500 mt-0.5 font-medium">Manage and organize your data in one place.</p>
              </div>
              <div className="flex justify-end items-center gap-2 w-full">
                {/* Global Search Bar */}
                <div className="relative flex-1 max-w-md mx-6" ref={searchRef}>
                  <div className="relative">
                    <IoSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={globalSearchQuery}
                      onChange={(e) => handleGlobalSearch(e.target.value)}
                      onFocus={() => { if (searchSuggestions.length > 0) setShowSearchDropdown(true); }}
                      placeholder="Search documents, folders, users..."
                      className="w-full pl-9 pr-9 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400 transition-all placeholder:text-slate-400 font-medium"
                    />
                    {globalSearchQuery && (
                      <button
                        onClick={() => { setGlobalSearchQuery(""); setSearchSuggestions([]); setShowSearchDropdown(false); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                        <IoClose size={16} />
                      </button>
                    )}
                  </div>
                  {/* Search Suggestions Dropdown */}
                  {showSearchDropdown && (
                    <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-2xl z-[100] max-h-[320px] overflow-y-auto"
                      style={{ boxShadow: "0 12px 40px -8px rgba(0,0,0,0.15)" }}
                    >
                      {searchLoading ? (
                        <div className="flex items-center justify-center gap-2 py-6">
                          <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                          <span className="text-xs text-slate-500 font-medium">Searching...</span>
                        </div>
                      ) : searchSuggestions.length === 0 ? (
                        <div className="py-6 text-center">
                          <p className="text-xs text-slate-400 font-medium">No results found</p>
                        </div>
                      ) : (
                        <div className="py-1.5">
                          {searchSuggestions.map((item, idx) => {
                            const isFolder = (item.fileType || item.type || "").toLowerCase() === "folder";
                            return (
                              <button
                                key={item.uploadID ?? item.folderDNSID ?? idx}
                                onClick={() => handleSearchSuggestionClick(item)}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-indigo-50 transition-colors group"
                              >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isFolder ? "bg-amber-50 text-amber-500" : "bg-indigo-50 text-indigo-500"}`}>
                                  {isFolder ? <FaFolder size={14} /> : <FaFileLines size={14} />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-indigo-700 transition-colors">
                                    {item.fileName || item.folderName || "Untitled"}
                                  </p>
                                  <p className="text-[11px] text-slate-400 font-medium mt-0.5 truncate">
                                    {item.fileType || "File"} {item.fileSize ? `· ${item.fileSize}` : ""}
                                  </p>
                                </div>
                                {/* <IoChevronForward size={14} className="text-slate-300 group-hover:text-indigo-400 shrink-0 transition-colors" /> */}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {effectivePermissions.isDelete && (
                  <button
                    data-tooltip-id="toolbar-tooltip"
                    data-tooltip-content="Trash Bin"
                    onClick={() => setShowTrashModal(true)}
                    className="flex items-center p-2 text-xs font-semibold bg-red text-white rounded-full transition-colors"
                  >
                    <BsTrash size={14} />
                  </button>
                )}
                {effectivePermissions.isEdit && (
                  <button
                    onClick={() => setShowAddOrg(true)}
                    className="crm-new-btn"
                  >
                    <FaPlus size={11} /> Add Organization
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 3-Column Layout */}
          <div className="xl:w-full h-[calc(100vh-138px)] relative pb-14">
            <div className="flex gap-4 h-full">

              {/* ── Left Column: All Organizations ── */}
              <div className="w-[280px] flex-shrink-0 bg-white border border-slate-100 rounded-2xl shadow-sm flex flex-col">
                <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100">
                  <h3 className="text-sm font-bold text-slate-800 tracking-wide ">All Organizations</h3>
                  <div className="flex items-center gap-1">
                    {effectivePermissions.isEdit && (
                      <button
                        data-tooltip-id="toolbar-tooltip"
                        data-tooltip-content="Add Organization"
                        onClick={() => setShowAddOrg(true)}
                        className="flex items-center p-2 text-xs font-semibold bg-[#4338ca] text-white rounded-full transition-colors"
                      >
                        <FaPlus size={13} />
                      </button>
                    )}
                    <button
                      data-tooltip-id="toolbar-tooltip"
                      data-tooltip-content={
                        orgSortOrder === "default"
                          ? "Sort A-Z"
                          : orgSortOrder === "asc"
                            ? "Sort Z-A"
                            : "Default Order"
                      }
                      onClick={() => {
                        setOrgSortOrder((prev) => {
                          if (prev === "default") return "asc";
                          if (prev === "asc") return "desc";
                          return "default";
                        });
                      }}
                      className={`flex items-center p-1.5 text-xs font-semibold rounded-full transition-colors text-white bg-[#4338ca]`}
                    >
                      {orgSortOrder === "desc" ? (
                        <BsSortAlphaDownAlt size={16} />
                      ) : (
                        <BsSortAlphaDown size={16} />
                      )}
                    </button>


                  </div>
                </div>

                {/* Org Search + Author Filter */}
                <div className="px-3 py-2.5 border-b border-slate-50 space-y-2">
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-xl px-3 py-1.5">
                    <IoSearch size={12} className="text-slate-400 shrink-0" />
                    <input
                      value={orgSearch}
                      onChange={(e) => setOrgSearch(e.target.value)}
                      placeholder="Search organizations..."
                      className="bg-transparent text-xs text-slate-700 placeholder-slate-400 flex-1 outline-none min-w-0"
                    />
                  </div>
                  {authorOptions.length > 1 && (
                    <select
                      value={authorFilter}
                      onChange={(e) => setAuthorFilter(e.target.value)}
                      className="w-full border border-slate-100 bg-slate-50 rounded-xl px-3 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 cursor-pointer font-medium"
                    >
                      <option value="">All Authors</option>
                      {authorOptions.map((name) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Org List */}
                <div className="flex-1 overflow-y-auto py-1.5 scroll-inner">
                  {orgsLoading ? (
                    <div className="space-y-2 px-3 py-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="flex items-center gap-3 px-2 py-2">
                          <div className="w-8 h-8 rounded-xl bg-slate-100 animate-pulse shrink-0" />
                          <div className="flex-1 space-y-1.5">
                            <div className="h-2.5 bg-slate-100 rounded animate-pulse w-3/4" />
                            <div className="h-2 bg-slate-100 rounded animate-pulse w-1/2" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : filteredOrgs.length === 0 ? (
                    <p className="text-center text-xs text-slate-400 py-8">No organizations found</p>
                  ) : (
                    filteredOrgs.map((org, idx) => (
                      <div
                        key={org.organizationsDNSId}
                        onClick={() => handleSelectOrg(org)}
                        className={`group flex items-center gap-3 px-3 py-2.5 mx-2 my-0.5 rounded-xl cursor-pointer transition-colors ${selectedOrg?.organizationsDNSId === org.organizationsDNSId
                          ? "bg-indigo-50 shadow-sm"
                          : "hover:bg-slate-50"
                          }`}
                      >
                        <div
                          className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-[11px] font-bold shrink-0 shadow-sm"
                          style={{ backgroundColor: getOrgColor(idx) }}
                        >
                          {getOrgInitials(org.organizationsName)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p
                            data-tooltip-id="toolbar-tooltip"
                            data-tooltip-content={org.organizationsName}
                            className={`truncate ${selectedOrg?.organizationsDNSId === org.organizationsDNSId ? "text-[15px] font-bold text-indigo-700" : "text-sm font-semibold text-slate-800"}`}
                          >
                            {org.organizationsName}
                          </p>
                          <p className={`mt-0.5 font-medium ${selectedOrg?.organizationsDNSId === org.organizationsDNSId ? "text-xs text-indigo-400" : "text-[11px] text-slate-400"}`}>
                            {org?.totalItem ?? '0'} Items
                          </p>
                        </div>
                        {(effectivePermissions.isEdit || effectivePermissions.isDelete) && (
                          <div className="relative" ref={orgContextMenuId === org.organizationsDNSId ? orgContextMenuRef : null}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (orgContextMenuId === org.organizationsDNSId) {
                                  setOrgContextMenuId(null);
                                } else {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setOrgContextMenuId(org.organizationsDNSId);

                                  const estimatedMenuHeight = 200;
                                  const spaceBelow = window.innerHeight - rect.bottom;

                                  if (spaceBelow < estimatedMenuHeight) {
                                    // Open upwards
                                    setOrgContextMenuPos({
                                      top: "auto",
                                      bottom: window.innerHeight - rect.top + 4,
                                      left: rect.left,
                                    });
                                  } else {
                                    // Open downwards
                                    setOrgContextMenuPos({
                                      top: rect.bottom + 4,
                                      bottom: "auto",
                                      left: rect.left,
                                    });
                                  }
                                }
                              }}
                              className={`p-1.5 rounded-lg transition-all ${orgContextMenuId === org.organizationsDNSId
                                ? "bg-indigo-50 text-indigo-600"
                                : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                                }`}
                            >
                              <BsThreeDotsVertical size={13} />
                            </button>
                            {orgContextMenuId === org.organizationsDNSId && (
                              <div className="fixed z-[100] w-48 bg-white border border-slate-100 rounded-2xl shadow-2xl py-2 overflow-hidden"
                                style={{
                                  top: orgContextMenuPos.top,
                                  bottom: orgContextMenuPos.bottom,
                                  left: orgContextMenuPos.left,
                                  boxShadow: "0 10px 40px -5px rgba(0,0,0,0.15), 0 4px 16px -4px rgba(0,0,0,0.1)"
                                }}
                              >
                                {effectivePermissions.isEdit && (
                                  <>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingOrg(org);
                                        setShowAddOrg(true);
                                        setOrgContextMenuId(null);
                                      }}
                                      className="w-full flex items-center gap-3 px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                                    >
                                      <span className="text-sm">✏️</span>
                                      <span>Edit</span>
                                    </button>

                                    {(user?.parentID === 0 && user?.userID === org?.userID) && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setMemberOrg(org);
                                          setShowManageMembers(true);
                                          setOrgContextMenuId(null);
                                        }}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                                      >
                                        <span className="text-slate-500">👥</span>
                                        <span>Manage Members</span>
                                      </button>
                                    )}
                                  </>
                                )}

                                {effectivePermissions.isDelete && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteOrg(org);
                                      setOrgContextMenuId(null);
                                    }}
                                    className="w-full flex items-center gap-3 px-4 py-2.5 text-xs text-red-500 hover:bg-red-50 transition-colors text-left font-medium"
                                  >
                                    <MdDeleteForever size={18} />
                                    <span>Delete</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {/* Org Count Footer */}
                <div className="px-4 py-3 border-t border-slate-100 bg-white mt-auto rounded-b-2xl">
                  <p className="text-xs text-slate-500 font-medium text-center">
                    Total Organizations: <span className="font-bold text-indigo-600">{activeOrgs.length}</span>
                  </p>
                </div>
              </div>

              {/* ── Middle Column: Folder Structure ── */}
              <div className="w-[320px] flex-shrink-0 bg-white border border-slate-100 rounded-2xl shadow-sm flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <LuFolderTree size={16} className="text-indigo-500" />
                    <h3 className="text-sm font-bold text-slate-800 tracking-wide">Folder Structure</h3>
                  </div>
                  <div className="flex items-center gap-1">

                    {folderPermissions.isEdit && (
                      <button
                        data-tooltip-id="toolbar-tooltip"
                        data-tooltip-content="New Folder"
                        onClick={() => setShowNewFolder(true)}
                        className="flex items-center p-2 text-xs font-semibold bg-[#4338ca] text-white rounded-full transition-colors">
                        <FaPlus size={12}
                        />
                      </button>
                    )}
                    <div className="relative">
                      <button
                        data-tooltip-id="toolbar-tooltip"
                        data-tooltip-content="Action"
                        onClick={() => setShowFolderOptions(!showFolderOptions)}
                        className="flex items-center p-2 text-xs font-semibold bg-[#4338ca] text-white rounded-full transition-colors"
                      >
                        <BsThreeDotsVertical size={12} />
                      </button>
                      {showFolderOptions && (
                        <div className="absolute right-0 top-7 z-[90] w-44 bg-white border border-slate-100 rounded-xl shadow-xl py-1.5 animate-in fade-in duration-100">
                          {[
                            {
                              icon: <span className="text-[11px] font-bold">↕</span>,
                              label: "Expand All",
                              onClick: () => {
                                handleExpandAll();
                                setShowFolderOptions(false);
                              }
                            },
                            {
                              icon: <span className="text-[11px] font-bold">→</span>,
                              label: "Collapse All",
                              onClick: () => {
                                handleCollapseAll();
                                setShowFolderOptions(false);
                              }
                            },
                            {
                              icon: <span className="text-[10px] font-bold">A-Z</span>,
                              label: isSorted ? "Default Sorting" : "A→Z Sort Tree",
                              onClick: () => {
                                setIsSorted(!isSorted);
                                setShowFolderOptions(false);
                              }
                            },
                          ].map((a, i) => (
                            <button
                              key={i}
                              onClick={a.onClick}
                              className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors text-left font-medium"
                            >
                              {a.icon}<span>{a.label}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto py-2 px-2 scroll-inner">
                  {/* Org Root */}
                  <div className="mb-1">
                    {/* {selectedOrg?.organizationsName && (
                      <div
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors ${selectedFolder?.organizationsDNSId === selectedOrg?.organizationsDNSId && !selectedFolder?.folderDNSID
                          ? "bg-indigo-50 text-indigo-700 font-semibold"
                          : "hover:bg-indigo-50/60 text-slate-700"
                          }`}
                      // onClick={() => handleSelectOrg(selectedOrg)}
                      >
                        <FaChevronDown size={9} className={selectedFolder?.organizationsDNSId === selectedOrg?.organizationsDNSId && !selectedFolder?.folderDNSID ? "text-indigo-500" : "text-slate-400"} />
                        <FaFolderOpen size={13} className="text-indigo-500 shrink-0" />
                        <span className="text-[15px] font-bold text-indigo-700 truncate">{selectedOrg?.organizationsName || "Organization"}</span>
                      </div>
                    )} */}
                    {!selectedOrg ? (
                      <div className="px-3 py-8 text-center">
                        <LuFolderTree size={22} className="mx-auto text-slate-300 mb-2" />
                        <p className="text-xs text-slate-400 font-medium">
                          Select an organization to view its folders
                        </p>
                      </div>
                    ) : treeLoading ? (
                      <div className="space-y-1 px-2 py-1">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="h-6 bg-slate-100 rounded animate-pulse" />
                        ))}
                      </div>
                    ) : renderedTree.length === 0 ? (
                      <div className="px-3 py-8 text-center">
                        <LuFolderTree size={22} className="mx-auto text-slate-300 mb-2" />
                        <p className="text-xs text-slate-400 font-medium">No folders yet</p>
                      </div>
                    ) : (
                      renderedTree.map((node) => (
                        <FolderTreeNode
                          key={node.folderDNSID ?? node.folderID}
                          node={node}
                          depth={1}
                          activeFolderId={selectedFolder?.folderDNSID ?? selectedFolder?.folderID}
                          onSelectFolder={handleSelectFolder}
                          onRenameFolder={openRenameModal}
                          onDeleteFolder={handleDeleteUpload}
                          onManageMembers={user?.parentID === 0 ? openFolderManageMembers : undefined}
                          expandedFolderIds={expandedFolderIds}
                          toggleFolderExpanded={toggleFolderExpanded}
                          isSorted={isSorted}
                          permissions={folderPermissions}
                        />
                      ))
                    )}
                  </div>
                </div>

                {/* Organization Storage Footer */}
                {selectedOrg && (
                  <div className="bg-white border-t border-slate-100 p-2">
                    <div className="bg-[#f8fafc] border border-slate-100 rounded-2xl p-4 flex justify-between">
                      <h4 className="text-sm font-bold text-slate-800">Organization Storage</h4>
                      <p className="text-xs font-semibold text-slate-500 mt-1">
                        {((orgStorageDetails?.totalSize ?? 0)).toFixed(1)} GB
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Right Column: Folder/File Detail View ── */}
              <div className="flex-1 min-w-0 bg-white border border-slate-100 rounded-2xl shadow-sm flex flex-col overflow-hidden">
                {/* Breadcrumb + Toolbar */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 gap-3 flex-wrap">
                  <div className="flex items-center gap-1 text-sm text-slate-500 font-medium flex-wrap">
                    {breadcrumb.map((crumb, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <IoChevronForward size={14} className="text-slate-300 shrink-0" />}
                        <span
                          className={`${i === breadcrumb.length - 1 ? "text-[15px] text-indigo-700 font-bold" : "hover:text-slate-700 cursor-pointer transition-colors"}`}
                          onClick={() => {
                            if (i === breadcrumb.length - 1) return; // already on this folder
                            if (crumb.node === null) {
                              // Clicked on org root
                              handleSelectOrg(selectedOrg);
                            } else {
                              handleSelectFolder(crumb.node);
                            }
                          }}
                        >
                          {crumb.name}
                        </span>
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {/* Hidden file input — accepts ALL file types, multiple */}
                    <input
                      ref={uploadInputRef}
                      type="file"
                      multiple
                      accept="*/*"
                      className="hidden"
                      onChange={handleUpload}
                    />
                    {folderPermissions.isEdit && (
                      <button
                        data-tooltip-id="toolbar-tooltip"
                        data-tooltip-content="Upload"
                        onClick={() => uploadInputRef.current?.click()}
                        disabled={uploadFilesMutation.isPending || !selectedOrg}
                        className="flex items-center p-2 text-xs font-semibold bg-[#4338ca] text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <BsUpload size={15} />
                      </button>
                    )}
                    {folderPermissions.isEdit && (
                      <button
                        data-tooltip-id="toolbar-tooltip"
                        data-tooltip-content="New Folder"
                        onClick={() => setShowNewFolder(true)}
                        className="flex items-center p-2 text-xs font-semibold bg-[#4338ca] text-white rounded-full transition-colors"
                      >
                        <LuFolderPlus size={15} />
                      </button>
                    )}
                    {folderPermissions.isDelete && selectedUploadIds.length > 0 && (
                      <button
                        data-tooltip-id="toolbar-tooltip"
                        data-tooltip-content="Delete"
                        onClick={() => handleDeleteUpload()}
                        className="flex items-center p-1.5 text-xs font-semibold bg-red text-white rounded-full transition-colors">
                        <MdDeleteForever size={16} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Folder Info */}
                {selectedFolder?.folderName && (
                  <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-50 bg-slate-50/40">
                    <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                      <BsFillFolderFill size={20} className="text-indigo-500" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-slate-900 leading-tight">{selectedFolder.folderName}</h2>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {totalItems} items
                        {selectedFolder?.updatedDate && (
                          <> · Updated {new Date(selectedFolder.updatedDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</>
                        )}
                        {/* {!selectedFolder?.updatedDate && <> · Updated Jul 9, 2026</>} */}
                      </p>
                    </div>
                  </div>
                )}

                {/* Search + Table */}
                <div className="flex-1 overflow-hidden flex flex-col">
                  {/* Table Search bar */}
                  <div className="px-5 py-3 border-b border-slate-50">
                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-xl px-3 py-1.5 max-w-sm">
                      <IoSearch size={13} className="text-slate-400 shrink-0" />
                      <input
                        value={fileSearch}
                        onChange={(e) => { setFileSearch(e.target.value); setPage(1); }}
                        placeholder="Search files..."
                        className="bg-transparent text-xs text-slate-700 placeholder-slate-400 flex-1 outline-none"
                      />
                    </div>
                  </div>

                  {/* Table */}
                  <div className="flex-1 overflow-auto scroll-inner">
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b border-slate-100">
                          <th className="px-4 py-3 w-10">
                            <input
                              type="checkbox"
                              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                              checked={paginatedFiles.length > 0 && selectedUploadIds.length === paginatedFiles.length}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUploadIds(paginatedFiles.map(f => f.uploadID));
                                } else {
                                  setSelectedUploadIds([]);
                                }
                              }}
                            />
                          </th>
                          {/* Date Modified */}
                          {["Name", "Type", "Author", "Status", "Actions"].map((h, i) => (
                            <th key={h} className={`px-4 py-3 text-sm font-medium text-slate-500 tracking-wide ${i === 5 ? "text-right" : ""}`}>
                              {h}{h === "Name" && <span className="ml-1 text-slate-300 font-bold">↕</span>}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {paginatedFiles.length > 0 ? paginatedFiles.map((file) => {
                          const isLocked = file?.isLocked;
                          const showOnlyView = (isLocked && user?.parentID > 0) || !folderPermissions.isEdit;
                          return (
                            <tr
                              key={file.uploadID}
                              className={`transition-colors border-b border-slate-100/50 group ${isLocked ? "bg-[#fffbeb] hover:bg-[#fff5cc]" : "hover:bg-slate-50/60"
                                }`}
                            >
                              <td className="px-4 py-4 w-10">
                                <input
                                  type="checkbox"
                                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                  checked={selectedUploadIds.includes(file.uploadID)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedUploadIds(prev => [...prev, file.uploadID]);
                                    } else {
                                      setSelectedUploadIds(prev => prev.filter(id => id !== file.uploadID));
                                    }
                                  }}
                                />
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-3">
                                  <FileIcon type={file?.fileType} size={16} />
                                  <button
                                    onClick={() => handleFileOrFolderClick(file)}
                                    className="text-[15px] font-semibold text-slate-900 hover:text-indigo-600 transition-colors text-left focus:outline-none flex items-center gap-1.5 min-w-0"
                                  >
                                    <span
                                      // data-tooltip-id="toolbar-tooltip"
                                      // data-tooltip-content={file?.fileName}
                                      className="truncate max-w-[220px]"
                                    >
                                      {file?.fileName}
                                    </span>
                                    {isLocked && (
                                      <span className="text-sm shrink-0" title="Locked (Checked-out)">
                                        🔒
                                      </span>
                                    )}
                                  </button>
                                  {(file?.tags) && (
                                    <div className="flex items-center gap-1">
                                      {(file.tags).split(",").map((tag, idx) => {
                                        const trimmed = tag.trim();
                                        if (!trimmed) return null;
                                        return (
                                          <span
                                            key={idx}
                                            className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-50 text-indigo-600 border border-indigo-100/60 shrink-0"
                                          >
                                            {trimmed}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm text-slate-600 font-medium">{file?.fileType || "File"}</td>
                              <td className="px-4 py-4 text-sm text-slate-600 font-medium">{file?.userName || ""}</td>
                              <td className="px-4 py-4">
                                {file.fileType === 'Folder' ? '-' :
                                  <StatusBadge status={file?.status} />
                                }
                              </td>
                              {/* <td className="px-4 py-4 text-sm text-slate-500 font-medium">
                                {file.updatedDate ? (
                                  <>
                                    <span>{new Date(file.updatedDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                                    <br />
                                    <span className="text-[10px] text-slate-400 mt-0.5">{new Date(file.updatedDate).toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' })}</span>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </td> */}
                              <td className="px-4 py-3 text-right">
                                <div className="relative inline-block" ref={fileActionId === file.uploadID ? fileActionRef : null}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (fileActionId === file.uploadID) {
                                        setFileActionId(null);
                                      } else {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setFileActionId(file.uploadID);

                                        const estimatedMenuHeight = 300;
                                        const spaceBelow = window.innerHeight - rect.bottom;

                                        if (spaceBelow < estimatedMenuHeight) {
                                          // Open upwards
                                          setFileActionPos({
                                            top: "auto",
                                            bottom: window.innerHeight - rect.top + 4,
                                            right: window.innerWidth - rect.right
                                          });
                                        } else {
                                          // Open downwards
                                          setFileActionPos({
                                            top: rect.bottom + 4,
                                            bottom: "auto",
                                            right: window.innerWidth - rect.right
                                          });
                                        }
                                      }
                                    }}
                                    className={`p-1.5 rounded-lg transition-all ${fileActionId === file.uploadID
                                      ? "bg-indigo-50 text-indigo-600"
                                      : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                                      }`}
                                  >
                                    <BsThreeDotsVertical size={14} />
                                  </button>
                                  {fileActionId === file.uploadID && (
                                    <div className="fixed z-[100] w-52 bg-white border border-slate-100 rounded-2xl shadow-2xl py-2 overflow-hidden animate-in fade-in zoom-in-95 duration-100"
                                      style={{
                                        top: fileActionPos.top,
                                        bottom: fileActionPos.bottom,
                                        right: fileActionPos.right,
                                        boxShadow: "0 10px 40px -5px rgba(0,0,0,0.15), 0 4px 16px -4px rgba(0,0,0,0.1)"
                                      }}
                                    >
                                      {file.fileType === "Folder" ? (
                                        <>
                                          {/* Folder Specific Actions */}
                                          {[
                                            {
                                              icon: <span className="text-sm">📁</span>,
                                              label: "Open",
                                              onClick: () => {
                                                handleFileOrFolderClick(file);
                                                setFileActionId(null);
                                              }
                                            },
                                            folderPermissions.isEdit && {
                                              icon: <span className="text-sm">✏️</span>,
                                              label: "Rename",
                                              onClick: () => {
                                                openRenameModal(file.uploadID, file.fileName, "Folder");
                                                setFileActionId(null);
                                              }
                                            },
                                            folderPermissions.isEdit && {
                                              icon: <span className="text-sm">↔️</span>,
                                              label: "Move",
                                              onClick: () => {
                                                setMovingItem({ id: file.uploadID, name: file.fileName, type: "Folder" });
                                                setIsCopy(false);
                                                setTargetParentId("");
                                                setShowMoveModal(true);
                                                setFileActionId(null);
                                              }
                                            },
                                            folderPermissions.isEdit && {
                                              icon: <span className="text-sm">📋</span>,
                                              label: "Copy",
                                              onClick: () => {
                                                setMovingItem({ id: file.uploadID, name: file.fileName, type: "Folder" });
                                                setIsCopy(true);
                                                setTargetParentId("");
                                                setShowMoveModal(true);
                                                setFileActionId(null);
                                              }
                                            },
                                          ].filter(Boolean).map((a, i) => (
                                            <button
                                              key={i}
                                              onClick={a.onClick}
                                              className="w-full flex items-center gap-3 px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                                            >
                                              {a.icon}
                                              <span>{a.label}</span>
                                            </button>
                                          ))}
                                          {folderPermissions.isDelete && (
                                            <>
                                              <div className="h-px bg-slate-100 mx-3 my-1" />
                                              <button
                                                onClick={() => {
                                                  handleDeleteUpload(file.uploadID, file.fileName, "Folder");
                                                  setFileActionId(null);
                                                }}
                                                className="w-full flex items-center gap-3 px-4 py-2 text-xs text-red-500 hover:bg-red-50 transition-colors text-left font-medium"
                                              >
                                                <span className="text-sm">🗑️</span>
                                                <span>Delete</span>
                                              </button>
                                            </>
                                          )}
                                        </>
                                      )
                                        : (
                                          showOnlyView ? (
                                            <button
                                              onClick={() => {
                                                handleFileOrFolderClick(file);
                                                setFileActionId(null);
                                              }}
                                              className="w-full flex items-center gap-3 px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                                            >
                                              <span className="text-base">👁️</span>
                                              <span>View</span>
                                            </button>
                                          ) : (
                                            <>
                                              {/* File Specific Actions */}
                                              {[
                                                {
                                                  icon: <span className="text-base">👁️</span>,
                                                  label: "View",
                                                  onClick: () => {
                                                    handleFileOrFolderClick(file);
                                                    setFileActionId(null);
                                                  }
                                                },
                                                {
                                                  icon: <span className="text-sm">⬇️</span>,
                                                  label: "Download",
                                                  onClick: async () => {
                                                    const url = resolveMediaUrl(file);
                                                    if (!url) return;
                                                    setFileActionId(null);
                                                    const toastId = toast.loading(`Downloading ${file.fileName || "file"}...`);
                                                    try {
                                                      const response = await fetch(url);
                                                      const blob = await response.blob();
                                                      const blobUrl = window.URL.createObjectURL(blob);
                                                      const a = document.createElement("a");
                                                      a.href = blobUrl;
                                                      a.download = file.fileName || "download";
                                                      document.body.appendChild(a);
                                                      a.click();
                                                      document.body.removeChild(a);
                                                      window.URL.revokeObjectURL(blobUrl);
                                                      toast.success("Download complete!", { id: toastId });
                                                    } catch {
                                                      toast.error("Download failed. Opening in new tab...", { id: toastId });
                                                      window.open(url, "_blank");
                                                    }
                                                  }
                                                },
                                                {
                                                  icon: <span className="text-sm">✏️</span>,
                                                  label: "Rename",
                                                  onClick: () => {
                                                    openRenameModal(file.uploadID, file.fileName, "File");
                                                    setFileActionId(null);
                                                  }
                                                },
                                                {
                                                  icon: <span className="text-sm">↔️</span>,
                                                  label: "Move",
                                                  onClick: () => {
                                                    setMovingItem({ id: file.uploadID, name: file.fileName, type: "File" });
                                                    setIsCopy(false);
                                                    setTargetParentId("");
                                                    setShowMoveModal(true);
                                                    setFileActionId(null);
                                                  }
                                                },
                                                {
                                                  icon: <span className="text-sm">📋</span>,
                                                  label: "Copy",
                                                  onClick: () => {
                                                    setMovingItem({ id: file.uploadID, name: file.fileName, type: "File" });
                                                    setIsCopy(true);
                                                    setTargetParentId("");
                                                    setShowMoveModal(true);
                                                    setFileActionId(null);
                                                  }
                                                },
                                              ].map((a, i) => (
                                                <button
                                                  key={i}
                                                  onClick={a.onClick}
                                                  className="w-full flex items-center gap-3 px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                                                >
                                                  {a.icon}
                                                  <span>{a.label}</span>
                                                </button>
                                              ))}
                                              <div className="h-px bg-slate-100 mx-3 my-1" />
                                              {[
                                                user?.parentID === 0 && {
                                                  icon: file.status === 'Published' ? <span className="text-sm">📥</span> : <span className="text-sm">📤</span>,
                                                  label: file.status === 'Published' ? 'Unpublish' : 'Publish',
                                                  onClick: () => {
                                                    handleToggleUploadStatus(file.uploadID, file.status);
                                                    setFileActionId(null);
                                                  }
                                                },
                                                user?.parentID === 0 && {
                                                  icon: (file.isLocked) ? <span className="text-sm">🔓</span> : <span className="text-sm">🔒</span>,
                                                  label: (file.isLocked) ? 'Unlock' : 'Lock (Check-out)',
                                                  onClick: () => {
                                                    handleToggleUploadLock(file.uploadID, file.isLocked);
                                                    setFileActionId(null);
                                                  }
                                                },
                                                {
                                                  icon: <span className="text-sm">🏷️</span>,
                                                  label: "Add Tags",
                                                  onClick: () => {
                                                    setTagsModal({ id: file.uploadID, name: file.fileName, tags: file.tags || "" });
                                                    setTagsValue(file.tags || "");
                                                    setFileActionId(null);
                                                  }
                                                },
                                              ].filter(Boolean).map((a, i) => (
                                                <button
                                                  key={i}
                                                  onClick={a.onClick}
                                                  className="w-full flex items-center gap-3 px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 hover:text-indigo-700 transition-colors text-left font-medium"
                                                >
                                                  {a.icon}
                                                  <span>{a.label}</span>
                                                </button>
                                              ))}
                                              {folderPermissions.isDelete && (
                                                <>
                                                  <div className="h-px bg-slate-100 mx-3 my-1" />
                                                  <button
                                                    onClick={() => {
                                                      handleDeleteUpload(file.uploadID, file.fileName, "File");
                                                      setFileActionId(null);
                                                    }}
                                                    className="w-full flex items-center gap-3 px-4 py-2 text-xs text-red-500 hover:bg-red-50 transition-colors text-left font-medium"
                                                  >
                                                    <span className="text-sm">🗑️</span>
                                                    <span>Delete</span>
                                                  </button>
                                                </>
                                              )}
                                            </>
                                          )
                                        )}
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        }) : (
                          <tr>
                            <td colSpan={6} className="px-4 py-12 text-center">
                              <div className="flex flex-col items-center gap-3 text-slate-400">
                                <FaFolderOpen size={32} className="text-slate-200" />
                                <p className="text-sm font-medium">No files found</p>
                                <p className="text-xs">Try a different search or select another folder</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50/30 flex-shrink-0">
                    <p className="text-[11px] text-slate-500 font-medium">
                      Showing {(page - 1) * perPage + 1} to {Math.min(page * perPage, totalItems)} of {totalItems} items
                    </p>
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs transition-colors"
                      >
                        ‹
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold transition-colors ${page === p ? "bg-indigo-600 text-white shadow-sm" : "border border-slate-200 text-slate-600 hover:bg-slate-100"}`}
                        >
                          {p}
                        </button>
                      ))}
                      <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed text-xs transition-colors"
                      >
                        ›
                      </button>
                      <select className="ml-1 text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-600 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30">
                        <option>10</option>
                        <option>20</option>
                        <option>50</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddOrg && (
        <AddOrgModal
          onClose={() => {
            setShowAddOrg(false);
            setEditingOrg(null);
          }}
          onSave={handleAddOrg}
          editingOrg={editingOrg}
          isPending={addUpdateOrgMutation.isPending}
        />
      )}
      {showNewFolder && (
        <NewFolderModal
          onClose={() => setShowNewFolder(false)}
          onSave={handleAddFolder}
          isPending={addUpdateFolderMutation.isPending}
        />
      )}

      {/* Move Folder / File Modal */}
      <MoveUploadModal
        movingItem={movingItem}
        selectedOrg={selectedOrg}
        folderTree={folderTree}
        targetParentId={targetParentId}
        setTargetParentId={setTargetParentId}
        isCopy={isCopy}
        onClose={() => {
          setShowMoveModal(false);
          setMovingItem(null);
        }}
        onMove={handleMoveConfirm}
        isPending={moveUploadMutation.isPending || copyUploadMutation.isPending}
      />

      {/* Rename Modal */}
      <RenameUploadModal
        renameModal={renameModal}
        renameValue={renameValue}
        setRenameValue={setRenameValue}
        onClose={() => setRenameModal(null)}
        onRename={handleRenameConfirm}
        isPending={renameUploadMutation.isPending}
      />

      {/* Preview Modal */}
      <PreviewUploadModal
        previewFile={previewFile}
        onClose={() => setPreviewFile(null)}
      />

      {/* Tags Modal */}
      <RenameUploadModal
        renameModal={tagsModal}
        renameValue={tagsValue}
        setRenameValue={setTagsValue}
        onClose={() => setTagsModal(null)}
        onRename={handleUpdateTags}
        isPending={updateUploadTagsMutation.isPending}
        title={tagsModal ? `Add Tags — ${tagsModal.name}` : ""}
        label="Tags (comma separated)"
        placeholder="e.g. testing, finance, raw"
        saveLabel="Save"
      />

      {/* Trash Modal */}
      <TrashBinModal
        isOpen={showTrashModal}
        onClose={() => setShowTrashModal(false)}
        selectedOrg={selectedOrg}
        activeTab={trashActiveTab}
        setActiveTab={setTrashActiveTab}
        deletedOrgs={deletedOrgs}
        trashData={trashData}
        trashLoading={trashLoading}
        deleteOrgMutation={deleteOrgMutation}
        deleteForeverOrgMutation={deleteForeverOrgMutation}
        deleteUploadMutation={deleteUploadMutation}
        deleteForeverUploadMutation={deleteForeverUploadMutation}
        onRestoreOrg={handleRestoreOrg}
        onDeleteOrgForever={handleDeleteOrgForever}
        onRestoreUpload={handleRestoreUpload}
        onDeleteUploadForever={handleDeleteUploadForever}
      />

      {/* Manage Members Modal */}
      <ManageMembersModal
        isOpen={showManageMembers}
        memberOrg={memberOrg}
        memberFolder={memberFolder}
        visibleAssignedUsers={visibleAssignedUsers}
        allUsers={allUsers}
        selectedUserToAssign={selectedUserToAssign}
        setSelectedUserToAssign={setSelectedUserToAssign}
        memberPermissions={memberPermissions}
        toggleMemberPermission={toggleMemberPermission}
        rolePermLoading={rolePermLoading}
        mutateAssignedUserMutation={mutateAssignedUserMutation}
        onAddMember={handleAddMember}
        onRemoveMember={handleRemoveMember}
        onClose={resetMemberModal}
      />
      <Tooltip
        id="toolbar-tooltip"
        place="bottom"
        backgroundColor="#4338ca"
        type="warning"
        effect="solid"
        style={{ zIndex: 9999, borderRadius: '8px', fontSize: '11px', fontWeight: 600, padding: '4px 10px', backgroundColor: '#4338ca' }}
      />
    </>
  );
};

export default OrganizationsDNS;
